// https://boost.allnetru.dev/api/documentation/organizer#/Landings
import { createApi } from '@reduxjs/toolkit/query/react';
import {
  GetOrganizerLandingResponse,
  GetOrganizerLandingFilterByIdResponse,
  PatchOrganizerLandingStatusByIdResponse,
  GetOrganizerLandingsParams,
  GetOrganizerLandingByIdResponse,
} from './models';
import { getQueryStringFromParams } from 'lib.shared/src/utils';
import { ModerationFiltersKeys } from './models';
import { SetupLanding } from 'lib.store/src/type-organizer/models';
import { ReusableSetupLanding } from './models';
import { API_EVENT_TAGS } from 'lib.store/src/tags';
import { organizerEvents } from '../events/events.api';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';

export const organizerLandings = createApi({
  reducerPath: API_EVENT_TAGS.ORGANIZER_LANDINGS,
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    API_EVENT_TAGS.ORGANIZER_LANDINGS,
    API_EVENT_TAGS.EVENTS_SETUP_PROGRESS,
    API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID,
    API_EVENT_TAGS.ORGANIZER_EVENTS,
  ],
  endpoints: (build) => ({
    getOrganizerLandings: build.query<GetOrganizerLandingResponse, GetOrganizerLandingsParams>({
      query: ({ filters, page }) => {
        const statusesQueryString = getQueryStringFromParams(
          filters?.statuses,
          ModerationFiltersKeys.statuses,
        );
        const partnersQueryString = getQueryStringFromParams(
          filters?.partners,
          ModerationFiltersKeys.partners,
        );
        const eventsQueryString = getQueryStringFromParams(
          filters?.events,
          ModerationFiltersKeys.events,
        );
        const pageQueryString = page ? `page=${page}` : '';

        const queryString = [
          statusesQueryString,
          partnersQueryString,
          eventsQueryString,
          pageQueryString,
        ]
          .filter(Boolean)
          .join('&');

        const params = {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          'filters[search]': filters?.search || undefined,
        };
        return {
          url: `/organizer/landings${queryString.length ? '?' + queryString : ''}`,
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
          params,
        };
      },
      providesTags: [{ type: API_EVENT_TAGS.ORGANIZER_LANDINGS, id: 'LIST' }],
    }),
    patchOrganizerLandingStatusById: build.mutation<
      PatchOrganizerLandingStatusByIdResponse,
      {
        landing: number;
        status: number;
        transition: number;
        reason?: number;
        comment?: string;
      }
    >({
      query: ({ landing, status, transition, reason, comment }) => {
        const body = {
          transition,
          ...(reason ? { reason } : {}),
          ...(comment ? { comment } : {}),
        };
        return {
          url: `/organizer/landings/${landing}/status/${status}`,
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body,
        };
      },
      invalidatesTags: [{ type: API_EVENT_TAGS.ORGANIZER_LANDINGS, id: 'LIST' }],
    }),
    getOrganizerLandingById: build.query<GetOrganizerLandingByIdResponse, { landingId: number }>({
      query: ({ landingId }) => ({
        url: `/organizer/landings/${landingId}`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      }),
    }),
    getOrganizerLandingFilterById: build.query<
      GetOrganizerLandingFilterByIdResponse,
      { search?: string; page?: number; eventId: string }
    >({
      query: ({ search, page, eventId }) => {
        const params = new URLSearchParams();

        if (search) {
          params.append('filters[search]', search);
        }
        if (page) {
          params.append('filters[page]', page.toString());
        }
        const queryString = params.toString();
        return {
          url: `/organizer/events/${eventId}/landings/filter${queryString.length ? '?' + queryString : ''}`,
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
          params,
        };
      },
      providesTags: [{ type: API_EVENT_TAGS.ORGANIZER_LANDINGS, id: 'LIST' }],
    }),
    getOrganizerSetupLandings: build.query<{ data: SetupLanding[] }, { eventId: string }>({
      query: ({ eventId }) => {
        return {
          url: `/organizer/events/${eventId}/setup/landings`,
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [{ type: API_EVENT_TAGS.ORGANIZER_LANDINGS, id: 'LIST' }],
    }),
    getOrganizerSetupLandingById: build.query<
      { data: SetupLanding },
      { eventId: string; landingId: number }
    >({
      query: ({ eventId, landingId }) => ({
        url: `/organizer/events/${eventId}/setup/landings/${landingId}`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: (_result, _error, { landingId }) => [
        { type: API_EVENT_TAGS.ORGANIZER_LANDINGS, id: landingId },
      ],
    }),
    createOrganizerSetupLanding: build.mutation<
      { data: SetupLanding },
      {
        eventId: string;
        body: {
          styles?: string | null;
          fonts?: string | null;
          header?: string | null;
          footer?: string | null;
          scripts?: string | null;
          tracks?: string | null;
        };
      }
    >({
      query: ({ eventId, body }) => ({
        url: `/organizer/events/${eventId}/setup/landings`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body,
      }),
      invalidatesTags: [{ type: API_EVENT_TAGS.ORGANIZER_LANDINGS, id: 'LIST' }],
      async onQueryStarted({ eventId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            organizerEvents.util.invalidateTags([
              { type: API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID, id: eventId },
              { type: API_EVENT_TAGS.ORGANIZER_EVENTS },
            ]),
          );
        } catch {
          // If the query fails, we don't need to invalidate anything
        }
      },
    }),
    updateOrganizerSetupLanding: build.mutation<
      { data: SetupLanding },
      {
        eventId: string;
        landingId: number;
        body: {
          styles?: string | null;
          fonts?: string | null;
          header?: string | null;
          footer?: string | null;
          scripts?: string | null;
          tracks?: string | null;
        };
      }
    >({
      query: ({ eventId, landingId, body }) => ({
        url: `/organizer/events/${eventId}/setup/landings/${landingId}`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body,
      }),
      invalidatesTags: [{ type: API_EVENT_TAGS.ORGANIZER_LANDINGS, id: 'LIST' }],
      async onQueryStarted({ eventId, landingId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            organizerLandings.util.updateQueryData(
              'getOrganizerSetupLandingById',
              { eventId, landingId },
              () => data,
            ),
          );
          dispatch(
            organizerEvents.util.invalidateTags([
              { type: API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID, id: eventId },
              { type: API_EVENT_TAGS.ORGANIZER_EVENTS },
            ]),
          );
        } catch {
          // If the query fails, we don't need to invalidate anything
        }
      },
    }),
    getReusableSetupLandings: build.query<{ data: ReusableSetupLanding[] }, { eventId: string }>({
      query: ({ eventId }) => ({
        url: `/organizer/events/${eventId}/setup/landings/reusable`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: (_result, _error, { eventId }) => [
        { type: API_EVENT_TAGS.ORGANIZER_LANDINGS, id: `reusable-${eventId}` },
      ],
    }),
    reuseSetupLanding: build.mutation<
      { data: SetupLanding },
      { eventId: string; landingId: number }
    >({
      query: ({ eventId, landingId }) => ({
        url: `/organizer/events/${eventId}/setup/landings/reuse/${landingId}`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
      }),
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: API_EVENT_TAGS.ORGANIZER_LANDINGS, id: 'LIST' },
        { type: API_EVENT_TAGS.ORGANIZER_LANDINGS, id: `reusable-${eventId}` },
      ],
      async onQueryStarted({ eventId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            organizerEvents.util.invalidateTags([
              { type: API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID, id: eventId },
              { type: API_EVENT_TAGS.ORGANIZER_EVENTS },
            ]),
          );
        } catch {
          // If the query fails, we don't need to invalidate anything
        }
      },
    }),
    patchOrganizerSetupLandingStatusById: build.mutation<
      { data: SetupLanding },
      {
        landing: number;
        eventId: string;
        status: string;
        transition: number;
        comment?: string;
      }
    >({
      query: ({ landing, eventId, status, transition, comment }) => {
        const body = {
          transition,
          ...(comment ? { comment } : {}),
        };
        return {
          url: `/organizer/events/${eventId}/setup/landings/${landing}/status/${status}`,
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body,
        };
      },
      invalidatesTags: [{ type: API_EVENT_TAGS.ORGANIZER_LANDINGS, id: 'LIST' }],
      async onQueryStarted({ eventId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            organizerEvents.util.invalidateTags([
              { type: API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID, id: eventId },
              { type: API_EVENT_TAGS.ORGANIZER_EVENTS },
            ]),
          );
        } catch {
          // If the query fails, we don't need to invalidate anything
        }
      },
    }),
  }),
});

export const {
  useGetOrganizerLandingsQuery,
  useGetOrganizerSetupLandingsQuery,
  useGetOrganizerSetupLandingByIdQuery,
  useCreateOrganizerSetupLandingMutation,
  useUpdateOrganizerSetupLandingMutation,
  usePatchOrganizerLandingStatusByIdMutation,
  usePatchOrganizerSetupLandingStatusByIdMutation,
  useGetOrganizerLandingFilterByIdQuery,
  useLazyGetOrganizerLandingByIdQuery,
  useGetReusableSetupLandingsQuery,
  useReuseSetupLandingMutation,
} = organizerLandings;
